import GQLQueryHelper from '../GQLQueryHelper'
import queries from '../queryInputs'
import utilities from '../utilities'

/**
 * @param {string} queryName
 * @returns {string[][]}
 */
function getInputStrings(queryName) {
  const inputs = queries[queryName]

  if (!inputs) {
    throw new Error(
      `Missing param definition for query/mutation named '${queryName}'`,
    )
  }

  const inputsTopLevel = []
  const inputsInner = []
  Object.entries(inputs).forEach(tuple => {
    inputsTopLevel.push(`$${tuple[0]}:${tuple[1]}`)
    inputsInner.push(`${tuple[0]}:$${tuple[0]}`)
  })

  return [inputsTopLevel, inputsInner]
}

/**
 * @param {string} def
 * @returns {string}
 */
function getFragmentNameFromDefinition(def) {
  return def.split(' on ')[0].trim().substr(9)
}

class AbstractCollection {
  /**
   * @param {string?} fragmentName
   */
  constructor(fragmentName) {
    this.fragmentName = fragmentName || this.constructor.FragmentDefault

    if (!this.fragmentName) {
      throw new Error(
        'Must initialize with a fragment and missing default fragment definition for collection ' +
          this.constructor.name,
      )
    }
  }

  // -----------------------------------------------------------------------------------------------------------
  // Protected functions

  /**
   * @param {string} queryName
   * @param {*} inputs
   * @param {string} fragmentName
   * @param {QueryOptions?} options
   * @returns {Promise<Array<*>>}
   * @protected
   */
  async _list(queryName, inputs, fragmentName, options) {
    const retVal = await GQLQueryHelper.Instance().queryAll(
      this._buildListQuery(queryName, fragmentName),
      inputs,
      options,
    )

    return retVal.map(r => this.constructor.afterFind(r))
  }

  /**
   * @param {string} queryName
   * @param {*} inputs
   * @param {string} fragmentName
   * @param {QueryOptions?} options
   * @returns {Promise<GQLQueryIterator>}
   * @protected
   */
  async _iterate(queryName, inputs, fragmentName, options) {
    // TODO: options?
    return GQLQueryHelper.Instance().iterativeQuery(
      this._buildListQuery(queryName, fragmentName),
      inputs,
      this.constructor.afterFind.bind(this),
    )
  }

  /**
   * @param {string} queryName
   * @param {*} inputs
   * @param {string} fragmentName
   * @param {QueryOptions?} options
   * @returns {Promise<*>}
   * @protected
   */
  async _get(queryName, inputs, fragmentName, options) {
    // TODO: options?
    const retVal = await GQLQueryHelper.Instance().queryOnce(
      this._buildGetQuery(queryName, fragmentName),
      inputs,
    )

    return retVal ? this.constructor.afterFind(retVal) : retVal
  }

  /**
   * @param {string} queryName
   * @param {*} inputs
   * @param {string} fragmentName
   * @param {QueryOptions?} options
   * @returns {Promise<*>}
   * @protected
   */
  async _mutate(queryName, inputs, fragmentName, options) {
    // TODO: options?
    const retVal = await GQLQueryHelper.Instance().executeMutation(
      this._buildMutation(queryName, fragmentName),
      inputs,
    )

    return retVal ? this.constructor.afterFind(retVal) : retVal
  }

  /**
   * @param {string} queryName
   * @param {string} fragment
   * @returns {string}
   * @private
   */
  _buildListQuery(queryName, fragment) {
    const [inputsTopLevel, inputsInner] = getInputStrings(queryName)
    const fragmentName = getFragmentNameFromDefinition(fragment)

    return `
      ${fragment}
      query ${utilities.capitalize(queryName)}(${inputsTopLevel.join(', ')}) {
        ${queryName}(${inputsInner.join(', ')}) {
          items {
            ...${fragmentName}
          }
          nextToken
        }
      }
    `
  }

  /**
   * @param {string} queryName
   * @param {string} fragment
   * @returns {string}
   * @protected
   */
  _buildGetQuery(queryName, fragment) {
    const [inputsTopLevel, inputsInner] = getInputStrings(queryName)
    const fragmentName = getFragmentNameFromDefinition(fragment)

    return `
      ${fragment}
      query ${utilities.capitalize(queryName)}(${inputsTopLevel.join(', ')}) {
        ${queryName}(${inputsInner.join(', ')}) {
          ...${fragmentName}
        }
      }
    `
  }

  /**
   * @param {string} queryName
   * @param {string} fragment
   * @returns {string}
   * @protected
   */
  _buildMutation(queryName, fragment) {
    const [inputsTopLevel, inputsInner] = getInputStrings(queryName)
    const fragmentName = getFragmentNameFromDefinition(fragment)

    return `
      ${fragment}
      mutation ${utilities.capitalize(queryName)}(${inputsTopLevel.join(', ')}) {
        ${queryName}(${inputsInner.join(', ')}) {
          ...${fragmentName}
        }
      }
    `
  }

  // -----------------------------------------------------------------------------------------------------------
  // Static functions

  /**
   * @param {string?} fragmentName
   * @returns {AbstractCollection}
   */
  static as(fragmentName) {
    return new this(fragmentName)
  }
}


export default AbstractCollection
